import React, { type FC, useState } from 'react';
import {
  Button,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowRightRounded } from '@mui/icons-material';

import Div from '../../../../shared/ui/Div';
import Image from '../../../../shared/ui/Image';
import lingoLiftLogo from '../../../../assets/lingoLiftLessonLogo.svg';
import listenwiseLogo from '../../../../assets/listenwiseLessonLogo.svg';
import {
  isLingoLiftProduct,
  isListenWiseProduct,
} from '../../../../shared/utils/lingoLiftCompareConditions';
import { LingoLiftProduct } from '../../../../shared/types/common-enums';
import { type ILingoLiftTeacherClassesStudentProgress } from '../../module/types';

import styles from './styles';

interface ProgressBarProps {
  width: string;
  marginLeft: string;
  lineType: string;
  score: number;
}

const ProgressBar: FC<ProgressBarProps> = ({ width, marginLeft, lineType, score }) => (
  <Div
    sx={{
      ...styles.progressFilledZone,
      width,
      marginLeft,
      ...styles.lineType.base,
      ...styles.lineType[lineType],
    }}
  >
    <Typography sx={styles.progressPercent}>{score}%</Typography>
  </Div>
);

interface ILingoLiftClassesProgressCardProps extends ILingoLiftTeacherClassesStudentProgress {
  product: LingoLiftProduct;
}

const LingoLiftClassesProgressCard: FC<ILingoLiftClassesProgressCardProps> = ({
  product = LingoLiftProduct.LingoLift,
  schoolClassUrl,
  schoolClassName,
  students,
  studentsCountLabel,
  quizAveragePercent,
  scoreDistribution,
  averageScore,
  seeClassSummaryReportUrl,
  seeAllAssignedUrl,
}) => {
  const [isShowRosterClasses, setIsShowRosterClasses] = useState(false);

  const scoreDistributionLow = scoreDistribution?.low || 0;
  const scoreDistributionMid = scoreDistribution?.middle || 0;
  const scoreDistributionHigh = scoreDistribution?.high || 0;
  const averageScoreResult = averageScore?.result || 0;
  const averageScoreLevel = averageScore?.level || 'mid';

  const renderProgressBar = () => {
    if (isListenWiseProduct(product)) {
      return (
        <>
          {scoreDistributionLow > 0 && (
            <ProgressBar
              width={`${scoreDistributionLow}%`}
              marginLeft="0px"
              lineType="low"
              score={scoreDistributionLow}
            />
          )}
          {scoreDistributionMid > 0 && (
            <ProgressBar
              width={`calc(${scoreDistributionMid}% - 1px)`}
              marginLeft="1px"
              lineType="mid"
              score={scoreDistributionMid}
            />
          )}
          {scoreDistributionHigh > 0 && (
            <ProgressBar
              width={`calc(${scoreDistributionHigh}% - 1px)`}
              marginLeft="1px"
              lineType="hi"
              score={scoreDistributionHigh}
            />
          )}
        </>
      );
    }

    if (isLingoLiftProduct(product) && averageScoreResult > 0) {
      return (
        <ProgressBar
          width={`${averageScoreResult}%`}
          marginLeft="0px"
          lineType={averageScoreLevel}
          score={averageScoreResult}
        />
      );
    }

    return null;
  };

  const renderTable = () => {
    if (!isShowRosterClasses) return null;

    if (isListenWiseProduct(product)) {
      return (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 700 }} aria-label="customized table">
            <TableHead sx={styles.tableHead}>
              <TableRow>
                <TableCell>Student Name</TableCell>
                <TableCell align="right">Average score</TableCell>
                <TableCell align="right">Last quiz score</TableCell>
                <TableCell align="right">Last quiz name</TableCell>
                <TableCell align="right">Last quiz submitted at</TableCell>
                <TableCell align="right">Last quiz time spent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students?.map((row, index) => (
                <TableRow key={index}>
                  <TableCell component="th" scope="row">
                    <Link href={row.studentUrl}>{row.studentName}</Link>
                  </TableCell>
                  <TableCell align="right">
                    <Typography>
                      {row.averageScore?.level} {row.averageScore?.result}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography>{row.lastQuizQuizableName}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography>{row.lastQuizSubmittedAt}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography>{row.lastQuizTimeSpent}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    if (isLingoLiftProduct(product)) {
      return (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 700 }} aria-label="customized table">
            <TableHead sx={styles.tableHead}>
              <TableRow>
                <TableCell>Student Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students?.map((row, index) => (
                <TableRow key={index}>
                  <TableCell component="th" scope="row">
                    <Link href={row.studentUrl}>{row.studentName}</Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    return null;
  };

  return (
    <Div sx={styles.container}>
      <Grid container sx={{ ...styles.row, ...styles.rowSpace, ...styles.borderedRow }}>
        <Div sx={styles.logoAndCountWrapper}>
          <Div>
            {isLingoLiftProduct(product) && <Image src={lingoLiftLogo} alt="lingolift icon" />}
            {isListenWiseProduct(product) && <Image src={listenwiseLogo} alt="listenwise icon" />}
          </Div>
          <Typography>{studentsCountLabel}</Typography>
        </Div>
        <Div sx={styles.titleWrapper}>
          <Link href={schoolClassUrl} sx={styles.title}>
            {schoolClassName}
          </Link>
        </Div>
      </Grid>

      <Grid sx={{ ...styles.row, ...styles.contentRow }}>
        <Div sx={styles.coverageRow}>
          <Typography fontWeight={700}>
            {isListenWiseProduct(product) && (
              <>
                Class Quiz Average: {quizAveragePercent}%<br />
              </>
            )}
            {isLingoLiftProduct(product) && <>Class Completion Average: {averageScoreResult}%</>}
          </Typography>
          <Div sx={styles.actions}>
            <Link
              href={seeClassSummaryReportUrl}
              sx={{ ...styles.actionButton, ...styles.viewSummary }}
            >
              View Class Summary Report
            </Link>
            <Link href={seeAllAssignedUrl} sx={{ ...styles.actionButton, ...styles.viewAssigned }}>
              View All Assigned
            </Link>
          </Div>
        </Div>
        <Div sx={styles.progressContainer}>
          <Div sx={styles.progressWrapper}>{renderProgressBar()}</Div>
        </Div>

        <Button
          onClick={() => setIsShowRosterClasses(!isShowRosterClasses)}
          sx={styles.rosterButton}
          endIcon={<ArrowRightRounded />}
        >
          Class Roster
        </Button>

        {renderTable()}
      </Grid>
    </Div>
  );
};

export default LingoLiftClassesProgressCard;
